import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { TreeView, TreeViewRef } from 'devextreme-react/tree-view';
import { navigation } from '../../app-navigation';
import { useNavigation } from '../../contexts/navigation';
import { useScreenSize } from '../../utils/media-query';
import './SideNavigationMenu.scss';
import type { SideNavigationMenuProps } from '../../types';

import * as events from 'devextreme/events';
import { useAuth } from '../../contexts/auth';

import SessionDetail from '../session-detail/SessionDetail';

import { UserRoleOwner } from '../../classes/consts/UserRole';

export default function SideNavigationMenu(props: React.PropsWithChildren<SideNavigationMenuProps>) {
  const {
    children,
    selectedItemChanged,
    openMenu,
    compactMode,
    onMenuReady
  } = props;

  const { isLarge } = useScreenSize();

  const { sessionDetail } = useAuth();

  function normalizePath() {
    const pathVisibility: Record<string, boolean> = {
      '/billing': sessionDetail?.Role === UserRoleOwner,
      '/metaapis': !!sessionDetail?.WithMetaAPI,
      '/portal': !!sessionDetail?.WithPortal,
      '/assistant': !!sessionDetail?.WithInternalAIAssistant,
      '/ai-groups': !!sessionDetail?.WithCustomerAIAssistant,
      '/reporting-groups': !!sessionDetail?.WithAdvancedCustomerCredentials,
      '/global-settings': !!sessionDetail?.WithGlobalSettings,
      '/workspace-settings': !!sessionDetail?.WithWorkspaceSettings
    };
    const isVisible = (path?: string) => !path || !(path in pathVisibility) || pathVisibility[path];

    const navigationFiltered = navigation
        .filter((item) => item.enterpriseOnly === undefined || item.enterpriseOnly === !!sessionDetail?.EnterpriseMode)
        .filter((item) => isVisible(item.path))
        .map((item) => item.items ? { ...item, items: item.items.filter((subItem) => isVisible(subItem.path)) } : item)
        .filter((item) => !item.items || item.items.length > 0);

    return navigationFiltered.map((item) => (
      { ...item, expanded: isLarge, path: item.path && !(/^\//.test(item.path)) ? `/${item.path}` : item.path }
    ))
  }

  const items = useMemo(
    normalizePath,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sessionDetail]
  );

  const { navigationData: { currentPath } } = useNavigation();

  const treeViewRef = useRef<TreeViewRef>(null);
  const wrapperRef = useRef<HTMLDivElement>();
  const getWrapperRef = useCallback((element: HTMLDivElement) => {
    const prevElement = wrapperRef.current;
    if (prevElement) {
      events.off(prevElement, 'dxclick');
    }

    wrapperRef.current = element;
    events.on(element, 'dxclick', (e: React.PointerEvent) => {
      openMenu(e);
    });
  }, [openMenu]);

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance();
    if (!treeView) {
      return;
    }

    if (currentPath !== undefined) {
      treeView.selectItem(currentPath);
      treeView.expandItem(currentPath);
    }

    if (compactMode) {
      treeView.collapseAll();
    }
  }, [currentPath, compactMode]);

  return (
    <div
      className={'side-navigation-menu'}
      ref={getWrapperRef}
    >
      {children}
      <div className={'menu-container'}>
        <div>
          {sessionDetail?.EnterpriseMode || sessionDetail?.UserIsMultiCustomer ? <SessionDetail session={sessionDetail}/> : ""}
          <TreeView
          ref={treeViewRef}
          items={items}
          keyExpr={'path'}
          selectionMode={'single'}
          focusStateEnabled={false}
          expandEvent={'click'}
          onItemClick={selectedItemChanged}
          onContentReady={onMenuReady}
          width={'100%'}
          />
        </div>
      </div>
    </div>
  );
}
